/**
 * Sticky column helpers for dense ERP tables (A4).
 * Pure functions — testable without DOM.
 */

export type StickySide = 'left' | 'right' | null

export function stickyCellClass(opts: {
  side: StickySide
  isHeader?: boolean
}): string {
  if (!opts.side) return ''
  const z = opts.isHeader ? 'z-30' : 'z-20'
  const bg = opts.isHeader
    ? 'bg-slate-100'
    : 'bg-[var(--color-surface)] group-hover:bg-[var(--color-bg)] group-[.row-selected]:bg-[var(--color-accent-soft)]'
  const edge =
    opts.side === 'left'
      ? 'sticky left-0 border-r border-[var(--color-border)]'
      : 'sticky right-0 border-l border-[var(--color-border)]'
  return `${edge} ${bg} ${z}`
}

/**
 * Compute left/right pixel offsets for sticky columns in order.
 * widths[i] applies to stickyIds[i] (default 120 if missing).
 */
export function stickyOffsets(
  stickyIds: string[],
  widths: Record<string, number | undefined>,
  defaultWidth = 120,
): Record<string, number> {
  const out: Record<string, number> = {}
  let acc = 0
  for (const id of stickyIds) {
    out[id] = acc
    acc += widths[id] ?? defaultWidth
  }
  return out
}

/** Right-side offsets: last sticky id gets right:0. */
export function stickyRightOffsets(
  stickyIds: string[],
  widths: Record<string, number | undefined>,
  defaultWidth = 100,
): Record<string, number> {
  const out: Record<string, number> = {}
  let acc = 0
  for (let i = stickyIds.length - 1; i >= 0; i -= 1) {
    const id = stickyIds[i]!
    out[id] = acc
    acc += widths[id] ?? defaultWidth
  }
  return out
}

export function isStickyId(
  id: string,
  stickyLeftIds: string[],
  stickyRightIds: string[],
): StickySide {
  if (stickyLeftIds.includes(id)) return 'left'
  if (stickyRightIds.includes(id)) return 'right'
  return null
}
